import { useState, useEffect } from "react";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { orange, grey } from '@mui/material/colors';
import CssBaseline from '@mui/material/CssBaseline';

import { auth, rtdb } from './firebase.js'
import { onAuthStateChanged } from "firebase/auth";
import { ref, set, onValue, update } from "firebase/database";

import Header from './Header.js'
import Footer from './Footer.js'
import SignIn from './SignIn.js'
import MembersList from './MembersList.js'
import MemberInfo from './MemberInfo.js'
import MemberMovements from './MemberMovements.js'
import AdminMenu from './AdminMenu.js'
import MembersListNewDebitDialog from './MembersListNewDebitDialog.js'
import FamilyGroup from './FamilyGroup.js'
import ExpendeturesEl from './Expendetures.js'

import { Box, CircularProgress } from "@mui/material";

import Obj2StrArr from './Obj2StrArr.js'
import safeAddress from './SafeAddress.js'
import StrArr2Obj from "./StrArr2Obj.js";
import deepMerge from "./deepMerge.js";
import idb from './idb.js';
import dateString from "./DateString.js";

function Component(props) {

    const theme = createTheme({
        palette: {
            primary: orange,
            secondary: grey,
        },
    });

    const [isConnected, setIsConnected] = useState(false);
    const [dbConnected, setDbConnected] = useState(false);
    const [manuallyDiconnected, setManuallyDisconnected] = useState({ s: false, o: 'df' });
    const [pendingCount, setPendingCount] = useState([]);
    const [User, setDbUser] = useState({});
    const [Loaded, setLoaded] = useState(false);
    const [readyToRender, setReadyToRender] = useState(false);

    const [UsersList, setDbUsersList] = useState({});
    const [Members, setDbMembers] = useState({});
    const [Movements, setDbMovements] = useState({});
    const [FamilyGroups, setFamilyGroups] = useState({});
    const [Debits, setDbDebits] = useState({});
    const [Means, setDbMeans] = useState([]);
    const [Expendetures, setDbExpendetures] = useState({});

    const [checkedRows, setCheckedRows] = useState([]);
    const [showNewDebitDialog, setShowNewDebitDialog] = useState(false);
    const [newFamilyGroupId, setNewFamilyGroupId] = useState('');

    const [View, setView] = useState('members');
    const [activeMember, setActiveMember] = useState('');
    const [EscKeyStroke, setEscKeyStroke] = useState(false);
    const [readyToClose, setReadyToClose] = useState({});
    const [search, setSearch] = useState('');
    const [lastReceitpNumber, setLastReceiptNumber] = useState(0);

    useEffect(() => {
        setTimeout(() =>
            setReadyToRender(User.uid && !!Object.keys(Members).length && !!Object.keys(Movements).length && !!Object.keys(Debits).length)
            , 500)
    }, [User, Members, Movements, Debits])

    async function DbUpload(data) {
        if (data !== true && !Object.keys(data).length)
            return false;

        let pending = await idb.get('Pending') || {},
            prev = { ...pending };
        Object.assign(pending, data || {});
        if (JSON.stringify(prev) !== JSON.stringify(pending)) await idb.set('Pending', prev);
        setPendingCount(Object.keys(pending));
        if (isConnected && !!Object.keys(pending).length) {
            update(ref(rtdb), pending)
                .then(() => idb.set('Pending', {}).then(() => setPendingCount([])))
                .catch((error) => console.error('Failed to upload pending changes:', error));
        }
        return true;
    }

    function setUser(data) {
        idb.set('User', data);
        setDbUser(data);
    }

    function setUsersList(data) {
        idb.set('UsersList', data);
        setDbUsersList(data);
    }

    function setMembers(data) {
        if (data)
            idb.set('Members', data);
        offlineMembersUpdate(data);
    }

    function setMovements(data) {
        if (data)
            idb.set('Movements', data);
        offlineMovementsUpdate(data);
    }

    function setDebits(data) {
        if (data)
            idb.set('Debits', data);
        offlineDebitsUpdate(data);
    }

    function setMeans(data) {
        if (data)
            idb.set('Means', data);
        offlineMeansUpdate(data);
    }

    function setExpendetures(data) {
        if (data)
            idb.set('Expendetures', data);
        offlineExpendeturesUpdate(data);
    }

    useEffect(() => {
        let uploadsArray = [];
        for (let deb in Debits)
            if (!!Debits[deb] && !Debits[deb].nombre)
                Object.assign(uploadsArray, Obj2StrArr({ nombre: deb }, `/debits/${deb}`) || {});
        DbUpload(uploadsArray).then(r => r && offlineDebitsUpdate());
    }, [Debits])

    useEffect(() => {
        if (!Object.keys(Movements).length || !Object.keys(Debits).length) return;
        let moves = { ...Movements };
        let last = 0,
            today = new Date().toISOString().substring(0, 10),
            dcd = !!Debits.cutDate ? Number(Debits.cutDate.importe) : 31,
            debitCutDate = `${dcd < 10 ? '0' : ''}${dcd}`,
            modified = 0;
        for (let u in moves)
            for (let d in moves[u])
                for (let m in moves[u][d]) {
                    if (!!moves[u][d][m].recibo && Number(moves[u][d][m].recibo) > last)
                        last = Number(moves[u][d][m].recibo);
                    if (!moves[u][d][m].Estado && ['debito', 'vencido'].includes(moves[u][d][m].tipo)) {
                        let dueDate = moves[u][d][m].Vencimiento.split('-').map((d, i) => [d, d, debitCutDate][i]).join('-'),
                            deb = Debits[safeAddress(moves[u][d][m].Concepto)] || Debits[moves[u][d][m].Concepto] || {},
                            type = dueDate < today ? 'vencido' : 'debito',
                            ammount = !!deb.importe ? (Number((!!deb.importeB && dueDate < today) ? deb.importeB : deb.importe) || null) : null;
                        if (moves[u][d][m].tipo !== type) {
                            moves[u][d][m].tipo = type;
                            modified++;
                        }
                        if (!!ammount && ammount !== Number(moves[u][d][m].Monto)) {
                            moves[u][d][m].Monto = ammount
                            modified++;
                        }
                    }
                }
        setLastReceiptNumber(last)
        if (modified)
            offlineMovementsUpdate(moves);
    }, [Debits, Movements])

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape' || event.keyCode === 27)
                setEscKeyStroke(true);
        };
        window.addEventListener('keydown', handleEsc);
        return () =>
            window.removeEventListener('keydown', handleEsc);
    }, []);

    useEffect(() => {
        if (EscKeyStroke)
            window.setTimeout(() => setEscKeyStroke(false), 10);
    }, [EscKeyStroke,])

    useEffect(() => {
        if (EscKeyStroke && View === 'member' && readyToClose.MemberInfo && readyToClose.MemberMovements)
            window.location.hash = ``
    }, [readyToClose, View, EscKeyStroke, search])

    window.onhashchange = router

    useEffect(() => {
        const unsubscribe = onValue(ref(rtdb, ".info/connected"), (snap) => {
            setDbConnected(snap.val() === true);
        });
        return () => unsubscribe();
    }, [dbConnected])

    useEffect(() => { setIsConnected(dbConnected && !manuallyDiconnected.s) }, [dbConnected, manuallyDiconnected])

    useEffect(() => {
        DbUpload(true);
        Promise.all([idb.get('User'), idb.get('UsersList'), idb.get('Members'), idb.get('Movements'), idb.get('Debits'), idb.get('Means'), idb.get('Expendetures'), idb.get('manuallyDiconnected')])
            .then(([user, usersList, members, movements, debits, means, expendetures, md]) => {
                if (user) setDbUser(user);
                if (usersList) setDbUsersList(usersList);
                offlineMembersUpdate(members || {});
                offlineMovementsUpdate(movements || {});
                offlineDebitsUpdate(debits || {});
                offlineMeansUpdate(means || {});
                offlineExpendeturesUpdate(expendetures || {});
                setManuallyDisconnected({ s: !!md, o: 'db' });
            });
    }, [isConnected])

    useEffect(() => {
        if ((manuallyDiconnected.o || '') === 'user')
            idb.set('manuallyDiconnected', manuallyDiconnected.s)
    }, [manuallyDiconnected])

    function router() {
        let h = window.location.hash.replaceAll('#', '').split('?'),
            v = {
                socio: {
                    view: 'member',
                    fn: setActiveMember,
                },
                familia: {
                    view: 'familyGroup',
                    fn: setActiveMember,
                },
                admin: {
                    view: 'admin',
                    fn: () => { },
                },
                gastos: {
                    view: 'expendetures',
                    fn: () => { },
                }
            }[h[0].toLowerCase()] || {
                view: 'members',
                fn: () => { setActiveMember('') }
            },
            i = h[1] || '';
        setView(v.view);
        v.fn(i);
    }

    useEffect(() => router(), [Members])

    const setUserUid = (uid) => {
        if (isConnected) {
            setUser({ uid: uid });
            idb.set('uid', uid);
        }
    }

    useEffect(() => {
        setLoaded(true);
        if (Loaded) {
            // idb.get('manuallyDiconnected').then(m => setManuallyDisconnected(m))
            onAuthStateChanged(auth, (loggedUser) => {
                if (loggedUser) {
                    setUserUid(loggedUser.uid);
                    onValue(ref(rtdb, 'users/' + loggedUser.uid), (snapshot) => {
                        if (!snapshot.val()) {
                            set(ref(rtdb, "users/" + loggedUser.uid), {
                                name: loggedUser.displayName || '',
                                email: loggedUser.email || '',
                                admin: false
                            });
                        } else {
                            setUser({ ...snapshot.val(), uid: loggedUser.uid, loaded: true });
                            if (snapshot.val().categoria === 'admin')
                                onValue(ref(rtdb, 'users'), (snapshot) => {
                                    const data = snapshot.val();
                                    setUsersList(
                                        Object.keys(data || {}).map(
                                            uid => ({
                                                uid: uid,
                                                ...data[uid],
                                                perm:null,
                                            })
                                        )
                                    );
                                }, (error) => { console.error(error) });
                        }
                    }, (error) => { console.error(error) });
                    onValue(ref(rtdb, 'members'), (snapshot) => {
                        setMembers(snapshot.val() || {nodata:true});
                    }, (error) => { console.error(error) });
                    onValue(ref(rtdb, 'movements'), (snapshot) => {
                        setMovements(snapshot.val() || {nodata:true});
                    }, (error) => { console.error(error) });
                    onValue(ref(rtdb, 'debits'), (snapshot) => {
                        setDebits(snapshot.val() || {cutDate:{nombre:"cutDate",importe:15}});
                    }, (error) => { console.error(error) });
                    onValue(ref(rtdb, 'means'), (snapshot) => {
                        setMeans(snapshot.val() || {nodata:true});
                    }, (error) => { console.error(error) });
                    onValue(ref(rtdb, 'expendetures'), (snapshot) => {
                        setExpendetures(snapshot.val() || {nodata:true});
                    }, (error) => { console.error(error) });

                } else
                    setUser({});
            });
        }
    }, [Loaded]);

    // useEffect(() => {
    //     if (User.categoria === 'admin' && !Object.keys(UsersList).length) {
    //         onValue(ref(rtdb, 'users'), (snapshot) => {
    //             const data = snapshot.val();
    //             console.log(data);
    //             setUsersList(
    //                 Object.keys(data || {}).map(
    //                     uid => ({
    //                         uid: uid,
    //                         email: data[uid].email,
    //                         pic: data[uid].pic || '',
    //                         name: data[uid].name || '',
    //                         admin: data[uid].admin || false,
    //                         dataObj: data[uid] || {}
    //                     })
    //                 )
    //             );
    //         }, (error) => { console.error(error) });
    //     }
    // }, [User, UsersList])

    async function updatePendingCount() {
        let p = await idb.get('Pending');
        setPendingCount(Object.keys(p || {}));
        return p || {};
    }

    async function offlineMembersUpdate(members) {
        if (!members)
            members = await idb.get('Members') || {};
        const pending = await updatePendingCount();
        if (Object.keys(pending || {}).length > 0) {
            const pendingObj = await StrArr2Obj(pending);
            if (pendingObj.members) {
                members = deepMerge(members, pendingObj.members);
            }
        }
        if (Object.keys(Debits || {}).length > 1)
            for (let m in members)
                if (!Array.isArray(members[m].debitos))
                    members[m].debitos = (members[m].debitos || '').split('\n').map(i => {
                        for (let d in Debits) if (safeAddress(i) === safeAddress(d)) return d;
                        return '';
                    }).filter(i => i.length > 0);

        let FamilyGroupsObj = {};
        for (let m in members)
            if (!!members[m].grupo_familiar) {
                let fgid = 'FG-' + members[m].grupo_familiar;
                if (!members[fgid]) members[fgid] = { debitos: [] };
                let familyGroup = members[fgid] || {},
                    mergedDebits = [...new Set([...members[m].debitos || [], ...familyGroup.debitos || []])],
                    [memberDebits, familyDebits] = debitsFiltering(mergedDebits);
                members[m].debitos = memberDebits;
                members[fgid].debitos = familyDebits;
                if (!FamilyGroupsObj[members[m].grupo_familiar])
                    FamilyGroupsObj[members[m].grupo_familiar] = [];
                FamilyGroupsObj[members[m].grupo_familiar].push(m)
            }

        for (let id in FamilyGroupsObj) {
            let fg = FamilyGroupsObj[id].toSorted(),
                data = {
                    type: 'familia',
                    documentonumero: '',
                    apellido: '',
                    nombre: `x${fg.length}`,
                    socio: 'Grupo Familiar',
                    direccion: '',
                    ciudad: '',
                    codpostal: '',
                    debitos: !!members['FG-' + id] ? members['FG-' + id].debitos || [] : [],
                }
            for (let m of fg) {
                if (!!data.documentonumero) data.documentonumero += ', ';
                data.documentonumero += members[m].documentonumero;
                if (!data.apellido || data.apellido.length > members[m].apellido) data.apellido = members[m].apellido;
                if (!data.direccion && !!members[m].direccion) {
                    data.direccion = members[m].direccion;
                    data.ciudad = members[m].ciudad;
                    data.codpostal = members[m].codpostal;
                }
            }
            members[`FG-${id}`] = data;
        }
        setFamilyGroups(FamilyGroupsObj);
        setDbMembers(members);
        return true;
    }

    useEffect(() => { offlineMembersUpdate() }, [Debits])

    async function offlineMovementsUpdate(movements) {
        if (!movements)
            movements = await idb.get('Movements') || {};
        await updatePendingCount()
            .then(p => StrArr2Obj(p || {})
                .then(pendingObj =>
                    movements = deepMerge(movements, pendingObj.movements || {})
                )
            );
        setDbMovements(movements);
    }

    async function offlineDebitsUpdate(debits) {
        if (!debits)
            debits = await idb.get('Debits') || {};
        await updatePendingCount()
            .then(p => StrArr2Obj(p || {})
                .then(pendingObj =>
                    debits = deepMerge(debits, pendingObj.debits || {})
                )
            );
        setDbDebits(debits);
    }

    async function offlineMeansUpdate(means) {
        if (!means)
            means = await idb.get('Means') || {};
        await updatePendingCount()
            .then(p => StrArr2Obj(p || {})
                .then(pendingObj =>
                    means = deepMerge(means, pendingObj.means || {})
                )
            );
        setDbMeans(
            Object.keys(means).map(m => means[m])
        );
    }

    async function offlineExpendeturesUpdate(expendetures) {
        if (!expendetures)
            expendetures = await idb.get('Expendetures') || {};
        await updatePendingCount()
            .then(p => StrArr2Obj(p || {})
                .then(pendingObj =>
                    expendetures = deepMerge(expendetures, pendingObj.expendetures || {})
                )
            );
        setDbExpendetures(expendetures);
    }

    const cancelDebit = (mid, d, m) => {
        return Obj2StrArr({
            Estado: 'Anulado',
            DetEstado: `Anulado el ${dateString(1)}, por ${User.nombre} (${User.uid})`
        }, `/movements/${mid}/${d}/${m}`) || {};
    }

    const setCuotas = (
        MemberId = '',
        year = (new Date().getFullYear() + (new Date().getMonth() > 10 ? 1 : 0)),
        debits = !!Members[MemberId] ? Members[MemberId].debitos : []
    ) => {
        if (!MemberId) return;
        let debsList = {},
            movements = Movements[MemberId],
            pendingCuotas = {},
            today = new Date(new Date().toLocaleDateString().split('/').toReversed().map(n => ((Number(n) < 10 ? '0' : '') + n)).join('-')),
            uploadsArray = {};
        if (!Array.isArray(debits)) debits = debits.split('\n').filter(i => i.length > 0);
        for (let deb of debits)
            if (!!Debits[deb]) {
                debsList[Debits[deb].nombre] = deb;
                pendingCuotas[deb] = {};
                switch (Debits[deb].periodicidad) {
                    case 'Mensual':
                    case 'Mensual/Familiar':
                        for (let m = (year > new Date().getFullYear()) ? 1 : new Date().getMonth() + (new Date().getDate() > 1 ? 2 : 1); m < 13; m++)
                            pendingCuotas[deb][m] = 'pend'
                        break;
                    case 'Anual':
                        pendingCuotas[deb].anual = 'pend'
                        break;
                    default:
                        break;
                }
            }

        for (let d in movements)
            for (let m in movements[d])
                if (
                    !!movements[d][m].Vencimiento
                    && Number(movements[d][m].Vencimiento.split('-')[0]) === year
                ) {
                    let conc = safeAddress(movements[d][m].Concepto).toLowerCase(),
                        venc = Number(movements[d][m].Vencimiento.split('-')[1]),
                        remove = true;
                    for (let pend in pendingCuotas)
                        if (
                            conc === safeAddress(pend).toLowerCase()
                            && !['anulado'].includes((movements[d][m].Estado || '').toLowerCase())
                        ) {
                            let period = Debits[pend].periodicidad.toLowerCase().includes('anual') ? 'anual' : venc;
                            pendingCuotas[pend][period] = 'done';
                            remove = false;
                        }
                    if (remove && new Date(movements[d][m].Vencimiento) > today)
                        for (let deb in Debits)
                            if (
                                !['pagado', 'anulado'].includes((movements[d][m].Estado || '').toLowerCase())
                                &&
                                [safeAddress(deb).toLowerCase(), safeAddress(Debits[deb].nombre).toLowerCase()].includes(conc)
                            )
                                Object.assign(uploadsArray, cancelDebit(MemberId, d, m));
                }

        for (let deb in pendingCuotas) {
            let debit = Debits[deb];
            if (!debit)
                for (let d in Debits)
                    if (!debit && safeAddress(Debits[d].nombre) === safeAddress(deb))
                        debit = { ...Debits[d] };
            let data = {
                Concepto: debit.nombre,
                Fecha: dateString().substring(0, 10),
                Medio: 'Edición Usuario',
                Monto: Number(debit.importe),
                Timestamp: new Date(),
                UserId: User.uid,
                Usuario: User.nombre,
                montoOrig: Number(debit.importe),
                socio: MemberId,
                tipo: 'debito',
            };
            for (let per in pendingCuotas[deb])
                if (pendingCuotas[deb][per] === 'pend') {
                    let p = isNaN(per) ? new Date().getMonth() + 2 : Number(per),
                        tmonth = `${p < 10 ? '0' : ''}${p}`,
                        debitDetail = { ...data, Vencimiento: `${year}-${tmonth}-01` };
                    Object.assign(uploadsArray, Obj2StrArr(debitDetail, `/movements/${MemberId}/${debitDetail.Vencimiento}/${dateString().replaceAll('.', ',')}-${Object.keys(pendingCuotas).indexOf(deb)},${tmonth}`) || {});
                }
        }
        DbUpload(uploadsArray).then(r => r && offlineMovementsUpdate())
    }

    const debitsGeneration = () => {
        let members = (View === 'members' && checkedRows.length > 0) ? [...checkedRows] : [activeMember];
        if (!members.length) return;
        for (let m of members) {
            let fgid = !!Members[m].grupo_familiar ? ('FG-' + Members[m].grupo_familiar) : null;
            if (!!fgid && !members.includes(fgid) && !!Members[fgid])
                members.push(fgid);
        }
        let year = prompt('Ingrese el año para generar las cuotas', (new Date().getFullYear() + (new Date().getMonth() > 10 ? 1 : 0)));
        if (!year || !Number(year) || Number(year) < new Date().getFullYear()) return;
        members.map(m => setCuotas(m, Number(year)));
        if (View === 'members')
            window.alert('Debitos Generados')
    }

    const clearUploadCache = () => {
        if (window.location.hostname === 'localhost') {
            idb.set('Pending', {});
            setPendingCount([]);
            offlineMembersUpdate();
            offlineMovementsUpdate();
            offlineDebitsUpdate();
            offlineMeansUpdate();
            offlineExpendeturesUpdate();
        } else
            window.location.reload();
    }

    useEffect(() => {
        switch (`${View}|${activeMember.substring(0, 3)}`) {
            case 'member|FG-':
                window.location.hash = `familia?${activeMember.substring(3)}`;
                return;
            case 'familyGroup|DNI':
                window.location.hash = `socio?${activeMember}`;
                return;
            default:
                break;
        }
    }, [View, activeMember])

    useEffect(() => {
        let enabled = '';
        for (let m of checkedRows)
            if (!!Members[m].grupo_familiar)
                if (!enabled)
                    enabled = Members[m].grupo_familiar
                else if (enabled !== Members[m].grupo_familiar)
                    enabled = 'inconsistent';
        if (!enabled) enabled = btoa(new Date().toISOString());
        if (enabled === 'inconsistent') enabled = false;
        setNewFamilyGroupId(enabled);
    }, [checkedRows, Members])

    const AddMembersToFamilyGroup = () => {
        if (!newFamilyGroupId) return;
        let uploadsArray = {};
        for (let m of checkedRows)
            Object.assign(uploadsArray, Obj2StrArr({ grupo_familiar: newFamilyGroupId }, `/members/${m}`) || {});
        DbUpload(uploadsArray).then(r => r && offlineMembersUpdate());
        window.location.hash = `familia?${newFamilyGroupId}`;
    }

    const removeUserFromFamilyGroup = (memberId) => {
        let fg = Members[memberId].grupo_familiar;

        if (!fg) return;
        if (!window.confirm(FamilyGroups[fg].length > 2 ?
            `¿Desea eliminar al miembro ${Members[memberId].apellido} ${Members[memberId].nombre} del grupo familiar?` :
            `¿Desea disolver el grupo familiar ${Members['FG-' + fg].apellido}?`
        )) return;

        let membersList = FamilyGroups[fg].length > 2 ? [memberId] : FamilyGroups[fg],
            uploadsArray = {};

        for (let m of membersList)
            Object.assign(uploadsArray, Obj2StrArr({ grupo_familiar: null }, `/members/${m}`) || {});

        if (FamilyGroups[fg].length <= 2)
            window.location.hash = '';
        DbUpload(uploadsArray).then(r => r && offlineMembersUpdate());

    }

    const debitsFiltering = (debits) => [
        [...debits].filter(d => !!Debits[d] && !Debits[d].periodicidad.toLowerCase().includes('familiar')),
        [...debits].filter(d => !!Debits[d] && Debits[d].periodicidad.toLowerCase().includes('familiar'))
    ]

    const mergedMovements = m => {
        let fgid = (!!Members[m] && !!Members[m].grupo_familiar) ? ('FG-' + Members[m].grupo_familiar) : null;
        let MemberMovements = Movements[m] || {},
            fgMovements = !!fgid ? Movements[fgid] || {} : {},
            merged = deepMerge(MemberMovements, fgMovements);
        return merged || {};
    }

    async function DbBackup() {
        let filename = `AGR-Backup_${new Date(new Date() - 3 * 60 * 60 * 1000).toISOString().replaceAll('T', '_').replaceAll(':', '-').split('.')[0]}.json.gz`,
            jsonObject = {
                debits: Debits,
                expendetures: Expendetures,
                means: Means,
                members: Members,
                movements: Movements,
                users: UsersList
            }
        const jsonString = JSON.stringify(jsonObject, null, 2),
            textEncoder = new TextEncoder(),
            encodedData = textEncoder.encode(jsonString),
            blob = new Blob([encodedData], { type: 'application/json' }),
            compressedReadableStream = blob.stream().pipeThrough(new CompressionStream('gzip')),
            compressedBlob = await new Response(compressedReadableStream).blob(),
            url = URL.createObjectURL(compressedBlob),
            a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    async function DbBackupRestore() {
        if ((window.location.hash.split('?')[1] || '').toLowerCase() !== 'enabledbupload') {
            console.log('Remember to set the "enableDBupload" hash');
            return;
        }
        async function uploadData(data) {
            let uploadsArray = {};

            for (let m of data.means || [])
                uploadsArray[`/means/${safeAddress(m)}`] = m;

            let debits = data.debits || data.Debitos || {};
            for (let d in debits || {}) {
                let deb = debits[d];
                if (!deb.nombre) deb.nombre = d;
                Object.assign(
                    uploadsArray,
                    Obj2StrArr(deb || {}, `/debits/${safeAddress(deb.nombre)}`)
                    || {}
                );
            }

            Object.assign(
                uploadsArray,
                Obj2StrArr(data.expendetures || {}, `/expendetures`)
                || {}
            );

            Object.assign(
                uploadsArray,
                Obj2StrArr(data.members || data.Socios || {}, `/members`)
                || {}
            );

            let movements = data.movements || data.Movimientos || {};
            for (let m in movements)
                for (let d in movements[m])
                    for (let k in movements[m][d])
                        Object.assign(
                            uploadsArray,
                            Obj2StrArr(movements[m][d][k] || {}, `/movements/${m}/${d}/${k.replaceAll('.', ',')}`)
                            || {}
                        );

            if (window.confirm(`¿Confirma que desea escribir ${Object.keys(uploadsArray).length} registros en la base de datos?`)) {
                await DbBackup();
                await DbUpload(uploadsArray);
            }
            // offlineMeansUpdate();
        }
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json.gz,.json';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            try {
                let stream = file.stream();
                if (file.name.endsWith('.gz'))
                    stream = stream.pipeThrough(new DecompressionStream('gzip'));
                const response = new Response(stream);
                response.json().then(uploadData);
            } catch (err) {
                console.error(err);
                window.alert('Error al leer el archivo');
            }
        };
        input.click();
    }

    return (
        <ThemeProvider theme={theme}>
            <style>{`
.CustomDataGrid td:nth-of-type(1), .CustomDataGrid th:nth-of-type(1) {
    position: sticky;
    left: 0;
    z-index: 1;
    background-color: inherit;
}
@media only screen and (min-width: 800px) {
    .CustomDataGrid td:nth-of-type(2), .CustomDataGrid th:nth-of-type(2) {
        position: sticky;
        left: 50px;
        z-index: 1;
        background-color: inherit;
    }
    .CustomDataGrid td:nth-of-type(3), .CustomDataGrid th:nth-of-type(3) {
        position: sticky;
        left: 120px;
        z-index: 1;
        background-color: inherit;
    }
}`
            }</style>
            <CssBaseline />

            <Header
                isConnected={isConnected}
                pendingCount={pendingCount.length}
                User={User}
                View={View}
                checkedRows={(checkedRows || []).length > 0}
                newFamilyGroupId={newFamilyGroupId}
                setShowNewDebitDialog={setShowNewDebitDialog}
                debitsGeneration={debitsGeneration}
                AddMembersToFamilyGroup={AddMembersToFamilyGroup}
                clearUploadCache={clearUploadCache}
                manuallyDiconnected={manuallyDiconnected}
                setManuallyDisconnected={setManuallyDisconnected}
            />

            {!User.uid && (
                <SignIn />
            )}

            {User.uid && !readyToRender && (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: -1,
                        }}>
                        <CircularProgress
                            variant="determinate"
                            size='50vmin'
                            value={25 * (!!User.uid + !!Object.keys(Members).length + !!Object.keys(Movements).length + !!Object.keys(Debits).length)}
                            sx={{
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '1%',
                                    left: '1%',
                                    right: '1%',
                                    bottom: '1%',
                                    borderRadius: '50%',
                                    background: 'currentcolor',
                                    opacity: .25,
                                    zIndex: -1,
                                },
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '2%',
                                    left: '2%',
                                    right: '2%',
                                    bottom: '2%',
                                    borderRadius: '50%',
                                    background: 'white'
                                }
                            }}
                        />
                    </Box>
                    <img style={{ height: '5em' }} src="/logo.png" alt="logo" />
                    <style>{`@keyframes blink { 0% {opacity: .2;} 20% {opacity: 1;} 100% {opacity: .2;} }`}</style>
                    <Box
                        sx={{
                            '& span': {
                                animationName: 'blink',
                                animationDuration: '1.4s',
                                animationIterationCount: 'infinite',
                                animationFillMode: 'both',
                                display: 'inline-block',
                                paddingLeft: '.1em',
                            },
                            '& span+span': { animationDelay: '0.2s' },
                            '& span+span+span': { animationDelay: '0.4s' }
                        }}
                    >
                        Cargando
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                    </Box>

                </Box>
            )}

            {readyToRender && View === 'members' && (
                <MembersList
                    User={User}
                    Members={Members}
                    FamilyGroups={FamilyGroups}
                    search={search}
                    setSearch={setSearch}
                    EscKeyStroke={EscKeyStroke}
                    setCheckedRows={setCheckedRows}
                />
            )}
            {readyToRender && View === 'members' && showNewDebitDialog && Object.keys(Debits).length > 0 && checkedRows.length > 0 && (
                <MembersListNewDebitDialog
                    setShowNewDebitDialog={setShowNewDebitDialog}
                    checkedRows={checkedRows}
                    Debits={Debits}
                    Members={Members}
                    debitsFiltering={debitsFiltering}
                    DbUpload={DbUpload}
                    offlineMembersUpdate={offlineMembersUpdate}
                />
            )}

            {readyToRender && View === 'member' && (
                <Box sx={{
                    flexGrow: 1,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    width: '100%',
                    height: '1em',
                }}>
                    <MemberInfo
                        uid={User.uid}
                        MemberData={{
                            ...(Members[activeMember] || {}),
                            debitos: Members[activeMember] ? [
                                ...(Members[activeMember] || {}).debitos || [],
                                ...(Members['FG-' + (Members[activeMember] || {}).grupo_familiar || ''] || {}).debitos || [],
                            ] : []
                        }}
                        MemberId={activeMember}
                        DbUpload={DbUpload}
                        Members={Members}
                        setCuotas={setCuotas}
                        offlineMembersUpdate={offlineMembersUpdate}
                        EscKeyStroke={EscKeyStroke}
                        Debits={Debits}
                        debitsFiltering={debitsFiltering}
                        readyToClose={status => setReadyToClose(prev => ({ ...prev, MemberInfo: status }))}
                    />
                    <MemberMovements
                        User={User}
                        MemberData={Members[activeMember] || {}}
                        FamilyGroupData={(Members[activeMember] && Members[activeMember].grupo_familiar) ? Members['FG-' + Members[activeMember].grupo_familiar] || {} : {}}
                        MovementsData={mergedMovements(activeMember)}
                        MemberId={activeMember}
                        Means={Means}
                        lastReceitpNumber={lastReceitpNumber}
                        cancelDebit={cancelDebit}
                        DbUpload={DbUpload}
                        offlineMovementsUpdate={offlineMovementsUpdate}
                        EscKeyStroke={EscKeyStroke}
                        readyToClose={status => setReadyToClose(prev => ({ ...prev, MemberMovements: status }))}
                    />
                </Box>
            )}
            {readyToRender && View === 'familyGroup' && !!FamilyGroups[activeMember] && (
                <FamilyGroup
                    User={User}
                    groupId={`FG-${activeMember}`}
                    fGroup={FamilyGroups}
                    Members={Object.fromEntries(FamilyGroups[activeMember].map(m => [m, Members[m] || {}]))}
                    Movements={Object.fromEntries([`FG-${activeMember}`, ...FamilyGroups[activeMember]].map(m => [m, Movements[m] || {}]))}
                    Means={Means}
                    lastReceitpNumber={lastReceitpNumber}
                    cancelDebit={cancelDebit}
                    DbUpload={DbUpload}
                    offlineMovementsUpdate={offlineMovementsUpdate}
                    EscKeyStroke={EscKeyStroke}
                    readyToClose={status => setReadyToClose(prev => ({ ...prev, FamilyGroup: status }))}
                    removeUserFromFamilyGroup={removeUserFromFamilyGroup}
                />
            )}

            {readyToRender && User.categoria === 'admin' && View === 'admin' && (
                <AdminMenu
                    UserUid={User.uid}
                    Debits={Debits}
                    Means={Means}
                    lastReceitpNumber={lastReceitpNumber}
                    offlineDebitsUpdate={offlineDebitsUpdate}
                    offlineMeansUpdate={offlineMeansUpdate}
                    EscKeyStroke={EscKeyStroke}
                    DbUpload={DbUpload}
                    DbBackup={DbBackup}
                    DbBackupRestore={DbBackupRestore}
                    UsersList={UsersList}
                />
            )}
            {readyToRender && User.categoria === 'admin' && View === 'expendetures' && (
                <ExpendeturesEl
                    User={User}
                    Expendetures={Expendetures}
                    offlineExpendeturesUpdate={offlineExpendeturesUpdate}
                    EscKeyStroke={EscKeyStroke}
                    DbUpload={DbUpload}
                />
            )}
            <Footer />

        </ThemeProvider>
    );
}

export default Component